/**
 * Service Duration Distribution Chart Component
 *
 * Shows distribution of service durations across different time ranges
 */

import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ServiceDurationDistribution } from "../types/operations-types";
import { MetricTooltip } from "./metric-tooltip";

interface ServiceDurationChartProps {
  current: ServiceDurationDistribution[];
  previous: ServiceDurationDistribution[];
}

export function ServiceDurationChart({ current, previous }: ServiceDurationChartProps) {
  const { t } = useTranslation("reports");

  // Translate category helper
  const translateCategory = (category: string) => {
    const key = `charts.serviceDuration.categories.${category}` as const;
    return t(key, { defaultValue: category });
  };

  // Combine data for chart
  const chartData = current.map((curr) => {
    const prev = previous.find((p) => p.category === curr.category);
    return {
      category: translateCategory(curr.category),
      originalCategory: curr.category,
      currentCount: curr.count,
      previousCount: prev?.count || 0,
      currentPercentage: curr.percentage,
      previousPercentage: prev?.percentage || 0,
      currentAvgDuration: curr.averageDuration,
      previousAvgDuration: prev?.averageDuration || 0,
    };
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.serviceDuration.title")}
          <MetricTooltip content={t("charts.serviceDuration.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("charts.serviceDuration.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart - Left */}
          <div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="category"
                  className="text-xs"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                />
                <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ fontWeight: 600 }}
                  formatter={(value: number, name: string) => {
                    if (name === "currentCount") return [value, t("period.current")];
                    if (name === "previousCount") return [value, t("period.previous")];
                    return [value, name];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  iconType="rect"
                  formatter={(value) =>
                    value === "currentCount" ? t("period.current") : t("period.previous")
                  }
                />
                <Bar dataKey="currentCount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="previousCount" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Comparison Table - Right */}
          <div className="space-y-2">
            {chartData.map((item) => {
              const countChange = item.currentCount - item.previousCount;
              const percentageChange =
                item.previousCount > 0
                  ? ((item.currentCount - item.previousCount) / item.previousCount) * 100
                  : 0;

              return (
                <div key={item.category} className="flex items-center gap-3 text-xs">
                  <div className="flex-1">
                    <span className="font-medium">{item.category}</span>
                    <span className="text-muted-foreground ml-2">
                      ({t("common.avg")}: {item.currentAvgDuration.toFixed(0)} min)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>
                      {item.previousCount} → {item.currentCount}
                    </span>
                    {item.previousCount > 0 && (
                      <span
                        className={
                          countChange > 0 ? "text-green-600" : countChange < 0 ? "text-red-600" : ""
                        }
                      >
                        ({percentageChange > 0 ? "+" : ""}
                        {percentageChange.toFixed(0)}%)
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span>{item.currentPercentage.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
